import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import type { Auth, User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { User } from '../types';
import { getFirebaseAuth } from '../firebase/index';
import { fetchCurrentUser, createCurrentUser } from '../api/users';
import { loadUserProfiles, saveUserProfiles } from '../repositories/localUserProfileRepository';
import { upsertUserProfile } from '../repositories/userFirestoreRepository';

export type AuthView = 'login' | 'signup';

const fallbackDisplayName = (email?: string | null) => {
    if (!email) return 'User';
    const [namePart] = email.split('@');
    return namePart || 'User';
};

interface AuthContextType {
  users: User[];
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  backendUser: User | null;
  authReady: boolean;
  backendUserLoading: boolean;
  authError: string | null;
  showAuthScreen: boolean;
  authView: AuthView;
  isManualAuthFlow: boolean;
  setAuthError: (error: string | null) => void;
  setShowAuthScreen: (show: boolean) => void;
  setAuthView: (view: AuthView) => void;
  setBackendUser: (user: User | null) => void;
  setCurrentUser: (user: User | null) => void;
  setIsManualAuthFlow: (isManual: boolean) => void;
  persistUsers: (updater: (prev: User[]) => User[]) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [users, setUsers] = useState<User[]>(() => loadUserProfiles());
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [backendUser, setBackendUser] = useState<User | null>(null);
  const [backendUserLoading, setBackendUserLoading] = useState(false);
  
  const [authError, setAuthError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [authView, setAuthView] = useState<AuthView>('login');
  
  const authRef = useRef<Auth | null>(null);
  const lastVerifiedUserIdRef = useRef<string | null>(null);
  const pendingBackendUserCreationRef = useRef<string | null>(null);
  const isManualAuthFlowRef = useRef(false);
  const [isManualAuthFlowState, setIsManualAuthFlowState] = useState(false);

  const setIsManualAuthFlow = useCallback((val: boolean) => {
      isManualAuthFlowRef.current = val;
      setIsManualAuthFlowState(val);
  }, []);

  const persistUsers = useCallback((updater: (prev: User[]) => User[]) => {
    setUsers(prev => {
      const next = updater(prev);
      saveUserProfiles(next);
      return next;
    });
  }, []);

  const ensureUserProfile = useCallback((fbUser: FirebaseUser, nameOverride?: string): User => {
    const desiredName = (nameOverride ?? fbUser.displayName ?? fallbackDisplayName(fbUser.email)).trim() || fallbackDisplayName(fbUser.email);
    const desiredEmail = fbUser.email ?? '';

    const existing = users.find(user => user.id === fbUser.uid);
    if (existing) {
      if (existing.name !== desiredName || existing.email !== desiredEmail) {
        const updatedProfile: User = {
          ...existing,
          name: desiredName,
          email: desiredEmail,
        };
        persistUsers(prev => prev.map(user => (user.id === updatedProfile.id ? updatedProfile : user)));
        upsertUserProfile({ id: updatedProfile.id, name: updatedProfile.name, email: updatedProfile.email }).catch(err => {
          console.error('Failed to upsert user profile:', err);
        });
        return updatedProfile;
      }
      upsertUserProfile({ id: existing.id, name: existing.name, email: existing.email }).catch(err => {
        console.error('Failed to upsert user profile:', err);
      });
      return existing;
    }

    const newProfile: User = {
      id: fbUser.uid,
      name: desiredName,
      email: desiredEmail,
      workspaces: [],
    };
    persistUsers(prev => [...prev, newProfile]);
    upsertUserProfile({ id: newProfile.id, name: newProfile.name, email: newProfile.email }).catch(err => {
      console.error('Failed to upsert user profile:', err);
    });
    return newProfile;
  }, [users, persistUsers]);

  const resolveAuth = useCallback(async (): Promise<Auth> => {
    if (authRef.current) return authRef.current;
    const auth = await getFirebaseAuth();
    authRef.current = auth;
    return auth;
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;

    resolveAuth()
      .then(auth => {
        if (!mounted) return;
        unsubscribe = onAuthStateChanged(auth, fbUser => {
          setFirebaseUser(fbUser);
          if (fbUser) {
            const profile = ensureUserProfile(fbUser);
            setCurrentUser(profile);
          } else {
            setCurrentUser(null);
          }
          setAuthError(null);
          setAuthReady(true);
        });
      })
      .catch(error => {
        console.error('Failed to initialize Firebase Auth:', error);
        if (mounted) {
          setAuthError('Unable to initialize authentication. Please refresh the page.');
          setAuthReady(true);
        }
      });

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [resolveAuth, ensureUserProfile]);

  useEffect(() => {
    if (!authReady || !currentUser) {
      lastVerifiedUserIdRef.current = null;
      pendingBackendUserCreationRef.current = null;
      setBackendUser(null);
      setBackendUserLoading(false);
      return;
    }
    if (pendingBackendUserCreationRef.current === currentUser.id) return;
    if (lastVerifiedUserIdRef.current === currentUser.id) return;

    let cancelled = false;
    const verifyBackendUser = async () => {
      if (isManualAuthFlowRef.current) return;
      
      setBackendUserLoading(true);
      try {
        const user = await fetchCurrentUser();
        if (!cancelled) {
          lastVerifiedUserIdRef.current = currentUser.id;
          setBackendUser(user);
        }
      } catch (error) {
        if (cancelled) return;
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          const email = currentUser.email?.trim();
          if (!email) {
            const auth = await resolveAuth();
            await firebaseSignOut(auth);
            setAuthError('We could not retrieve an email for this account. Please sign up to create an account.');
            setAuthView('signup');
            return;
          }
          try {
            pendingBackendUserCreationRef.current = currentUser.id;
            const created = await createCurrentUser({
              name: currentUser.name ?? fallbackDisplayName(email),
              email,
            });
            if (!cancelled) {
              lastVerifiedUserIdRef.current = currentUser.id;
              setBackendUser(created);
              pendingBackendUserCreationRef.current = null;
              return;
            }
          } catch (createError) {
            pendingBackendUserCreationRef.current = null;
            if (axios.isAxiosError(createError) && createError.response?.status === 409) {
              const auth = await resolveAuth();
              await firebaseSignOut(auth);
              setAuthError('An account with this email already exists. Please sign in with your original method.');
              setAuthView('login');
              return;
            }
            console.error('Failed to auto-create user profile:', createError);
          }
          const auth = await resolveAuth();
          await firebaseSignOut(auth);
          setAuthError('User not found. Please sign up to create an account.');
          setAuthView('signup');
          return;
        }
        console.error('Failed to verify user profile:', error);
      } finally {
        if (!cancelled) {
          setBackendUserLoading(false);
        }
      }
    };
    
    void verifyBackendUser();
    return () => { cancelled = true; };
  }, [authReady, currentUser, resolveAuth]);

  const logout = useCallback(async () => {
    const auth = await resolveAuth();
    await firebaseSignOut(auth);
    setBackendUser(null);
    setCurrentUser(null);
    setFirebaseUser(null);
  }, [resolveAuth]);

  const value = {
    users,
    currentUser,
    firebaseUser,
    backendUser,
    authReady,
    backendUserLoading,
    authError,
    showAuthScreen,
    authView,
    isManualAuthFlow: isManualAuthFlowState,
    setAuthError,
    setShowAuthScreen,
    setAuthView,
    setBackendUser,
    setCurrentUser,
    setIsManualAuthFlow,
    persistUsers,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
